/**
 * GenAI Summary Hook
 * 
 * Custom hook for managing GenAI maintenance analysis data including loading,
 * error handling, and integration with timeline events.
 */

import { useState, useEffect, useCallback } from 'react';
import { MaintenanceAnalysisResponse, WorkOrderEvent, DateRange } from '../services/equipment/types';
import { maintenanceAnalysisService } from '../services/equipment/maintenance-analysis-service';
import { useEquipmentContext } from '../contexts/equipment-context';
import { useDateRange } from './use-date-range';

interface UseGenAISummaryOptions {
  /** Work order events from timeline */
  events?: WorkOrderEvent[];
  /** Whether timeline is loading */
  timelineLoading?: boolean;
  /** Whether to auto-trigger analysis when data is available */
  autoAnalyze?: boolean;
}

interface UseGenAISummaryReturn {
  /** GenAI analysis response */
  analysis: MaintenanceAnalysisResponse | null;
  /** Loading state */
  isLoading: boolean;
  /** Error message if any */
  error: string | null;
  /** Whether analysis has been performed */
  hasAnalysis: boolean;
  /** Whether analysis can be started (has required data) */
  canStartAnalysis: boolean;
  /** Function to start/refresh analysis */
  startAnalysis: () => Promise<void>;
  /** Function to clear analysis and reset state */
  clearAnalysis: () => void;
}

/**
 * Hook for managing GenAI maintenance analysis
 */
export const useGenAISummary = (options: UseGenAISummaryOptions = {}): UseGenAISummaryReturn => {
  const {
    events = [],
    timelineLoading = false,
    autoAnalyze = false
  } = options;

  // Get context data
  const { state } = useEquipmentContext();
  const { dateRange } = useDateRange();

  // Local state
  const [analysis, setAnalysis] = useState<MaintenanceAnalysisResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Extract work order IDs from events
  const workOrderIds = events.map(event => event.id).filter(Boolean);

  // Check if we have all required data for analysis
  const canStartAnalysis = !!(
    state.site &&
    state.apmId &&
    dateRange &&
    workOrderIds.length > 0 &&
    !timelineLoading
  );

  /**
   * Start or refresh GenAI analysis
   */
  const startAnalysis = useCallback(async () => {
    if (!canStartAnalysis || isLoading) return;

    setIsLoading(true);
    setError(null);

    try {
      const response = await maintenanceAnalysisService.getMaintenanceAnalysis(
        state.site!,
        state.apmId!,
        dateRange!,
        workOrderIds
      );

      setAnalysis(response);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch GenAI analysis';
      setError(errorMessage);
      setAnalysis(null);
    } finally {
      setIsLoading(false);
    }
  }, [canStartAnalysis, isLoading, state.site, state.apmId, dateRange, workOrderIds]);

  /**
   * Clear analysis and reset state
   */
  const clearAnalysis = useCallback(() => {
    setAnalysis(null);
    setError(null);
    setIsLoading(false);
  }, []);

  // Auto-analyze when data becomes available
  useEffect(() => {
    if (autoAnalyze && canStartAnalysis && !analysis && !isLoading && !error) {
      startAnalysis();
    }
  }, [autoAnalyze, canStartAnalysis, analysis, isLoading, error, startAnalysis]);

  // Clear analysis when critical parameters change
  useEffect(() => {
    const shouldClear = !state.site || !state.apmId || !dateRange || workOrderIds.length === 0;
    if (shouldClear && (analysis || error)) {
      clearAnalysis();
    }
  }, [state.site, state.apmId, dateRange, workOrderIds.length, analysis, error, clearAnalysis]);

  return {
    analysis,
    isLoading,
    error,
    hasAnalysis: !!analysis,
    canStartAnalysis,
    startAnalysis,
    clearAnalysis
  };
};
